import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import employeeService, { employeeKeys, type EmployeeRecord } from '@/services/employee.service';
import hrmsService, {
  hrmsKeys,
  type AllocationChangeResult,
  type AllocationRow,
} from '@/services/hrms.service';
import departmentService from '@/services/department.service';
import { useAuthStore } from '@/store/authStore';
import { useAllocationProjects } from './shared/useAllocationProjects';
import AllocationResultSummary from './shared/AllocationResultSummary';

const { Text } = Typography;

type Mode = 'add' | 'replace' | 'move';

const EXITED = new Set(['Resigned', 'Terminated']);
const OPEN_PARAMS = { status: 'open' };

const MODE_HELP: Record<Mode, string> = {
  add: 'Adds the project alongside existing allocations. The employee total must stay within 100%.',
  replace: 'Ends every current allocation the day before the start date, then allocates the new project.',
  move: 'Ends the allocation on the "from" project the day before the start date and moves it to the new project. Leave % blank to carry the same share and role over.',
};

const AllocationsBulkPage: React.FC = () => {
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const canManage =
    Boolean(user?.is_super_admin) ||
    Boolean(user?.permissions?.some((p) => p === 'hr.allocation.manage' || p === 'hr.employee.edit'));

  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>();
  const [locationFilter, setLocationFilter] = useState<string>();
  const [currentProjectFilter, setCurrentProjectFilter] = useState<string | undefined>(
    searchParams.get('project') || undefined
  );
  const [onlyFree, setOnlyFree] = useState(false);
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);

  const [mode, setMode] = useState<Mode>('add');
  const [fromProjectId, setFromProjectId] = useState<string>();
  const [projectId, setProjectId] = useState<string>();
  const [pct, setPct] = useState<number | null>(100);
  const [from, setFrom] = useState<Dayjs>(dayjs());
  const [to, setTo] = useState<Dayjs | null>(null);
  const [role, setRole] = useState('');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<AllocationChangeResult | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const projects = useAllocationProjects();

  const employeeParams = useMemo(
    () => ({
      department_id: departmentFilter,
      location_id: locationFilter,
      search: search.trim() || undefined,
    }),
    [departmentFilter, locationFilter, search]
  );
  const { data: employees = [], isLoading } = useQuery({
    queryKey: employeeKeys.list(employeeParams),
    queryFn: () => employeeService.list(employeeParams),
  });
  const { data: openAllocations = [] } = useQuery({
    queryKey: hrmsKeys.allocations(OPEN_PARAMS),
    queryFn: () => hrmsService.listAllocations(OPEN_PARAMS) as Promise<AllocationRow[]>,
  });
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'selection', 'employees-list'],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 5 * 60 * 1000,
  });
  const { data: locations = [] } = useQuery({
    queryKey: hrmsKeys.locations(),
    queryFn: () => hrmsService.listLocations(),
  });

  const deptNameById = useMemo(
    () => new Map(departments.map((d) => [d.id, d.department_name])),
    [departments]
  );

  const { allocByEmployee, pctByEmployee } = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    const byEmp = new Map<string, AllocationRow[]>();
    const pctMap = new Map<string, number>();
    for (const a of openAllocations) {
      const list = byEmp.get(a.employee_id) || [];
      list.push(a);
      byEmp.set(a.employee_id, list);
      if (a.effective_from <= today && (!a.effective_to || a.effective_to >= today)) {
        pctMap.set(a.employee_id, (pctMap.get(a.employee_id) || 0) + Number(a.allocation_pct || 0));
      }
    }
    return { allocByEmployee: byEmp, pctByEmployee: pctMap };
  }, [openAllocations]);
  const currentPct = (empId: string) => pctByEmployee.get(empId) || 0;

  const rows = useMemo(
    () =>
      employees.filter((e) => {
        if (EXITED.has(String(e.employment_status))) return false;
        const mine = allocByEmployee.get(e.id) || [];
        if (currentProjectFilter && !mine.some((a) => a.project_id === currentProjectFilter)) return false;
        if (onlyFree && (pctByEmployee.get(e.id) || 0) >= 100) return false;
        return true;
      }),
    [employees, allocByEmployee, pctByEmployee, currentProjectFilter, onlyFree]
  );

  const projectsInUse = useMemo(() => {
    const ids = new Set(openAllocations.map((a) => a.project_id).filter(Boolean) as string[]);
    return projects.all.filter((p) => ids.has(p.value));
  }, [openAllocations, projects.all]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['hrms', 'allocations'] });
  };

  const assignMutation = useMutation({
    mutationFn: () =>
      hrmsService.bulkAllocate({
        employee_ids: selectedIds.map(String),
        project_id: projectId!,
        allocation_pct: pct,
        effective_from: from.format('YYYY-MM-DD'),
        effective_to: to ? to.format('YYYY-MM-DD') : null,
        role_on_project: role.trim() || null,
        notes: notes.trim() || null,
        mode,
        from_project_id: mode === 'move' ? fromProjectId : null,
      }),
    onSuccess: (r) => {
      setResult(r);
      setSelectedIds([]);
      refresh();
    },
    onError: (e: any) => {
      const errs: string[] | undefined = e?.response?.data?.data?.errors;
      if (errs?.length) setErrors(errs);
      else message.error(e?.response?.data?.message || e?.message || 'Assignment failed');
    },
  });

  const ready =
    selectedIds.length > 0 &&
    Boolean(projectId) &&
    (mode === 'move' ? Boolean(fromProjectId) && fromProjectId !== projectId : pct !== null && pct > 0);

  const confirmAssign = () => {
    const target = projects.labelById.get(projectId!) || 'the project';
    const verb =
      mode === 'move'
        ? `Move ${selectedIds.length} employee(s) from ${projects.labelById.get(fromProjectId!)} to ${target}`
        : mode === 'replace'
          ? `Reassign ${selectedIds.length} employee(s) to ${target} (ending their current allocations)`
          : `Allocate ${selectedIds.length} employee(s) to ${target}`;
    modal.confirm({
      title: `${verb}?`,
      content: `Starting ${from.format('DD MMM YYYY')}${to ? `, until ${to.format('DD MMM YYYY')}` : ''}.`,
      okText: 'Confirm',
      onOk: () => assignMutation.mutateAsync(),
    });
  };

  const columns: ColumnsType<EmployeeRecord> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{`${r.first_name} ${r.last_name}`}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.employee_code}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Department',
      key: 'department',
      width: 180,
      render: (_, r) => (r.department_id ? deptNameById.get(r.department_id) || '—' : '—'),
    },
    {
      title: 'Current / scheduled projects',
      key: 'projects',
      render: (_, r) => {
        const mine = allocByEmployee.get(r.id) || [];
        if (!mine.length) return <Tag color="orange">Unallocated</Tag>;
        return (
          <Space size={[4, 4]} wrap>
            {mine.map((a) => (
              <Tooltip
                key={a.id}
                title={`${a.project_name || ''} · from ${a.effective_from}${a.effective_to ? ` to ${a.effective_to}` : ''}${a.role_on_project ? ` · ${a.role_on_project}` : ''}`}
              >
                <Tag color={a.display_status === 'Scheduled' ? 'blue' : 'green'}>
                  {a.project_code} · {a.allocation_pct}%
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Allocated today',
      key: 'pct',
      width: 130,
      align: 'right',
      render: (_, r) => {
        const p = currentPct(r.id);
        return <Text type={p > 100 ? 'danger' : p < 100 ? 'warning' : undefined}>{p}%</Text>;
      },
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Assign employees to projects"
          subtitle="Bulk allocate, reassign or move employees between projects"
          primaryActions={
            <Button icon={<UnorderedListOutlined />} onClick={() => navigate('/hr/allocations')}>
              All allocations
            </Button>
          }
        />
      }
    >
      {!canManage && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="You can view allocations but not change them (needs hr.allocation.manage or hr.employee.edit)."
        />
      )}

      {canManage && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} optionType="button">
              <Radio.Button value="add">Add project</Radio.Button>
              <Radio.Button value="replace">Replace all current</Radio.Button>
              <Radio.Button value="move">Move from a project</Radio.Button>
            </Radio.Group>
            <Text type="secondary">{MODE_HELP[mode]}</Text>
            <Space wrap align="center">
              <Text strong>
                {selectedIds.length ? `${selectedIds.length} employee(s) selected` : 'Select employees below'}
              </Text>
              {mode === 'move' && (
                <Select
                  placeholder="From project"
                  style={{ width: 300 }}
                  showSearch
                  optionFilterProp="label"
                  options={projectsInUse}
                  value={fromProjectId}
                  onChange={setFromProjectId}
                />
              )}
              <Select
                placeholder={mode === 'move' ? 'To project' : 'Project'}
                style={{ width: 320 }}
                showSearch
                optionFilterProp="label"
                options={projects.assignable}
                value={projectId}
                onChange={setProjectId}
                loading={projects.isLoading}
                notFoundContent={projects.isLoading ? 'Loading…' : 'No open projects'}
              />
              <InputNumber
                min={1}
                max={100}
                addonAfter="%"
                placeholder={mode === 'move' ? 'Same %' : '%'}
                style={{ width: 120 }}
                value={pct}
                onChange={(v) => setPct(v)}
              />
              <DatePicker
                value={from}
                onChange={(d) => setFrom(d || dayjs())}
                allowClear={false}
                format="DD MMM YYYY"
                placeholder="Start"
              />
              <DatePicker
                value={to}
                onChange={setTo}
                format="DD MMM YYYY"
                placeholder="End (optional)"
                disabledDate={(d) => d.isBefore(from, 'day')}
              />
              <Input
                placeholder="Role on project (optional)"
                style={{ width: 200 }}
                value={role}
                onChange={(e) => setRole(e.target.value)}
                maxLength={100}
              />
              <Input
                placeholder="Notes (optional)"
                style={{ width: 220 }}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
              />
              <Button type="primary" disabled={!ready} loading={assignMutation.isPending} onClick={confirmAssign}>
                {mode === 'move' ? 'Move' : mode === 'replace' ? 'Reassign' : 'Assign'}
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search name, code, email"
            allowClear
            prefix={<SearchOutlined />}
            style={{ width: 240 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="Department"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
            value={departmentFilter}
            onChange={setDepartmentFilter}
          />
          <Select
            placeholder="Location"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            options={locations.map((l: any) => ({ value: l.id, label: `${l.location_code} — ${l.location_name}` }))}
            value={locationFilter}
            onChange={setLocationFilter}
          />
          <Select
            placeholder="Currently on project"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 280 }}
            options={projectsInUse}
            value={currentProjectFilter}
            onChange={(v) => {
              setCurrentProjectFilter(v);
              setSelectedIds([]);
            }}
          />
          <Checkbox checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)}>
            Below 100% only
          </Checkbox>
        </Space>
        <Table<EmployeeRecord>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          rowSelection={
            canManage
              ? { selectedRowKeys: selectedIds, onChange: setSelectedIds, preserveSelectedRowKeys: true }
              : undefined
          }
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No employees" /> }}
        />
      </Card>

      <Modal
        title="Project assignment done"
        open={Boolean(result)}
        onCancel={() => setResult(null)}
        footer={<Button onClick={() => setResult(null)}>Close</Button>}
      >
        {result && <AllocationResultSummary result={result} />}
      </Modal>

      <Modal
        title="Assignment not saved"
        open={errors.length > 0}
        onCancel={() => setErrors([])}
        footer={<Button onClick={() => setErrors([])}>Close</Button>}
      >
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Nothing was changed. Resolve these and try again."
        />
        <ul style={{ maxHeight: 320, overflow: 'auto', paddingLeft: 18 }}>
          {errors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </Modal>
    </WorkspaceLayout>
  );
};

export default AllocationsBulkPage;
